/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {useIntl} from 'react-intl'
import {validatePassword} from '../../utils/password-utils'

export default function useChangePasswordFields({
    form: {
        control,
        formState: {errors}
    },
    prefix = ''
}) {
    const {formatMessage} = useIntl()

    const fields = {
        newPassword: {
            name: `${prefix}newPassword`,
            placeholder: formatMessage({
                defaultMessage: 'Password',
                id: 'use_reset_password_fields.placeholder.password'
            }),
            type: 'password',
            defaultValue: '',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter a valid password.',
                    id: 'use_reset_password_fields.error.required_password'
                }),
                validate: {
                    hasMinChars: (val) =>
                        validatePassword(val).hasMinChars ||
                        formatMessage({
                            defaultMessage:
                                'Password must contain at least 8 characters.',
                            id: 'use_registration_fields.error.minimum_characters'
                        }),
                    hasUppercase: (val) =>
                        validatePassword(val).hasUppercase ||
                        formatMessage({
                            defaultMessage:
                                'Password must contain at least one uppercase letter.',
                            id: 'use_registration_fields.error.uppercase_letter'
                        }),
                    hasLowercase: (val) =>
                        validatePassword(val).hasLowercase ||
                        formatMessage({
                            defaultMessage:
                                'Password must contain at least one lowercase letter.',
                            id: 'use_registration_fields.error.lowercase_letter'
                        }),
                    hasNumber: (val) =>
                        validatePassword(val).hasNumber ||
                        formatMessage({
                            defaultMessage:
                                'Password must contain at least one number.',
                            id: 'use_registration_fields.error.contain_number'
                        }),
                    hasSpecialChar: (val) =>
                        validatePassword(val).hasSpecialChar ||
                        formatMessage({
                            defaultMessage:
                                'Password must contain at least one special character.',
                            id: 'use_registration_fields.error.special_character'
                        }),
                    hasNoSpace: (val) =>
                        validatePassword(val).hasNoSpace ||
                        formatMessage({
                            defaultMessage: 'Not Include spaces.',
                            id: 'use_registration_fields.error.contain_number'
                        })
                }
            },
            error: errors[`${prefix}newPassword`],
            control
        },
        confirmPassword: {
            name: `${prefix}confirmPassword`,
            placeholder: formatMessage({
                defaultMessage: 'Confirm Password',
                id: 'use_reset_password_fields.placeholder.confirm_password'
            }),
            type: 'password',
            defaultValue: '',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please confirm your password.',
                    id: 'use_reset_password_fields.error.required_confirm_password'
                }),
                validate: (value) => {
                    if (
                        value !==
                        control._fields[`${prefix}newPassword`]._f.value
                    ) {
                        return formatMessage({
                            defaultMessage: 'Passwords do not match.',
                            id: 'use_reset_password_fields.error.passwords_do_not_match'
                        })
                    }
                }
            },
            error: errors[`${prefix}confirmPassword`],
            control
        }
    }

    return fields
}
